import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Edit, Trash2, Eye, EyeOff, Filter, Package } from "lucide-react";
import { productService } from "../../lib/productService";
import { brandService } from "../../lib/brandService";
import { toast, Toaster } from "sonner";
import Header from "../Header";
import ConfirmationModal from "../ui/ConfirmationModal";
import CreateProductModal from "./CreateProductModal";

const ProductManagement = () => {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Brand filtering
  const brandId = searchParams.get("brand");
  const [brandName, setBrandName] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchProducts = useCallback(async () => {
    console.log("🔄 Fetching products...");
    setLoading(true);

    try {
      const result = await productService.getAdminProducts();
      console.log("📊 Products fetch result:", result);

      if (result.success) {
        console.log(
          "✅ Products loaded successfully:",
          result.data.length,
          "products"
        );

        // If brand filter is applied, get brand's products
        if (brandId) {
          const brandResult = await brandService.getAllBrands();
          if (brandResult.success) {
            const brand = brandResult.data.find((b) => b.id === brandId);
            if (brand) {
              // Set brand name
              setBrandName(brand.name);
              // Filter products to show only those belonging to the brand
              const brandProductIds = brand.products?.map((p) => p.id) || [];
              const filteredProducts = result.data.filter((p) =>
                brandProductIds.includes(p.id)
              );
              setProducts(filteredProducts);
            } else {
              setBrandName(null);
              setProducts([]);
            }
          } else {
            setBrandName(null);
            setProducts([]);
          }
        } else {
          setBrandName(null);
          setProducts(result.data);
        }
      } else {
        console.error("❌ Failed to fetch products:", result.error);
        toast.error(`Failed to fetch products: ${result.error}`);
        setProducts([]);
      }
    } catch (error) {
      console.error("❌ Error fetching products:", error);
      toast.error(`Error loading products: ${error.message}`);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleCreate = () => {
    setSelectedProduct(null);
    setShowCreateModal(true);
  };

  const handleEdit = (productId) => {
    setSelectedProduct(productId);
    setShowEditModal(true);
  };

  const handleDelete = async (productId) => {
    const product = products.find((p) => p.id === productId);
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!productToDelete) return;

    console.log("🔄 Deleting product:", productToDelete);
    setIsDeleting(true);

    try {
      const result = await productService.deleteProduct(productToDelete.id);
      console.log("📊 Delete result:", result);

      if (result.success) {
        console.log("✅ Product deleted successfully");
        toast.success("Product deleted successfully");
        await fetchProducts();
        setShowDeleteModal(false);
        setProductToDelete(null);
      } else {
        console.error("❌ Failed to delete product:", result.error);
        toast.error(`Failed to delete product: ${result.error}`);
      }
    } catch (error) {
      console.error("❌ Error deleting product:", error);
      toast.error(
        `An error occurred while deleting the product: ${error.message}`
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setProductToDelete(null);
    setIsDeleting(false);
  };

  const handleModalSuccess = async () => {
    await fetchProducts();
    setShowCreateModal(false);
    setShowEditModal(false);
    setSelectedProduct(null);
  };

  const handleToggleStatus = async (productId, currentStatus) => {
    console.log("🔄 Toggling product status:", { productId, currentStatus });

    try {
      const result = await productService.toggleProductStatus(
        productId,
        !currentStatus
      );

      console.log("📊 Toggle status result:", result);

      if (result.success) {
        const successMessage = `Product ${!currentStatus ? "activated" : "deactivated"} successfully`;
        console.log("✅", successMessage);
        toast.success(successMessage);
        await fetchProducts();
      } else {
        console.error("❌ Failed to update product status:", result.error);
        toast.error(`Failed to update product status: ${result.error}`);
      }
    } catch (error) {
      console.error("❌ Error toggling product status:", error);
      toast.error(`Error updating product status: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#B8460E]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black bg-gradient-to-br from-black via-gray-900 to-black">
      <Header />

      {/* Sonner Toaster */}
      <Toaster
        position="top-right"
        theme="dark"
        toastOptions={{
          style: {
            background: "rgba(0, 0, 0, 0.8)",
            backdropFilter: "blur(16px)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            color: "white",
          },
        }}
      />

      {/* Animated background elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-20 left-20 w-64 h-64 bg-[#B8460E] rounded-full blur-3xl opacity-20 animate-pulse"></div>
        <div
          className="absolute bottom-20 right-20 w-96 h-96 bg-[#8B3A0E] rounded-full blur-3xl opacity-15 animate-pulse"
          style={{ animationDelay: "2s" }}
        ></div>
      </div>

      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white">
                {brandId && brandName
                  ? `${brandName} Products`
                  : "Product Management"}
              </h1>
              <p className="text-gray-400 mt-2">
                {brandId && brandName
                  ? `Manage products for ${brandName} brand`
                  : "Manage your products, pain points, and marketing materials"}
              </p>
              {brandId && (
                <div className="flex items-center gap-2 mt-2">
                  <Filter className="w-4 h-4 text-[#B8460E]" />
                  <span className="text-sm text-[#B8460E]">
                    Filtered by brand: {brandName}
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={handleCreate}
              className="bg-gradient-to-r from-[#B8460E] to-[#8B3A0E] hover:from-[#8B3A0E] hover:to-[#B8460E] text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create Product
            </button>
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {products.map((product) => (
              <div
                key={product.id}
                className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 group relative"
              >
                {/* Animated Background Elements */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  <div className="absolute w-32 h-32 bg-[#B8460E]/5 rounded-full blur-2xl animate-pulse -top-8 -right-8"></div>
                  <div className="absolute w-24 h-24 bg-[#8B3A0E]/5 rounded-full blur-xl animate-pulse -bottom-4 -left-4"></div>
                </div>

                {/* Product Image */}
                {product.main_image_url && (
                  <div className="relative">
                    <div className="aspect-square overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900">
                      <img
                        src={product.main_image_url}
                        alt={product.name}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.parentElement.innerHTML = `
                            <div class="w-full h-full flex items-center justify-center text-gray-500">
                              <div class="text-center">
                                <svg class="w-16 h-16 mx-auto mb-2 opacity-50" fill="currentColor" viewBox="0 0 20 20">
                                  <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" />
                                </svg>
                                <p class="text-xs text-gray-600">Image not available</p>
                              </div>
                            </div>
                          `;
                        }}
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                )}

                <div className="relative z-10 p-6">
                  {/* Product Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-white group-hover:text-[#B8460E] transition-colors">
                            {product.name}
                          </h3>
                          <div className="flex items-center gap-2">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                product.is_active
                                  ? "bg-green-500/20 text-green-400"
                                  : "bg-red-500/20 text-red-400"
                              }`}
                            >
                              {product.is_active ? "Active" : "Inactive"}
                            </span>
                          </div>
                        </div>
                      </div>
                      {product.short_description && (
                        <p className="text-gray-300 text-sm mb-4 line-clamp-2">
                          {product.short_description}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-between pt-4 border-t border-white/10">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(product.id)}
                        className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-all duration-200"
                        title="Edit Product"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          handleToggleStatus(product.id, product.is_active)
                        }
                        className={`p-2 rounded-lg transition-all duration-200 ${
                          product.is_active
                            ? "bg-red-500/20 text-red-400 hover:bg-red-500/30"
                            : "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                        }`}
                        title={product.is_active ? "Deactivate" : "Activate"}
                      >
                        {product.is_active ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-all duration-200"
                        title="Delete Product"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {products.length === 0 && (
            <div className="text-center py-16">
              <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                {brandId && brandName
                  ? `No products in ${brandName}`
                  : "No products yet"}
              </h3>
              <p className="text-gray-400 mb-6">
                {brandId && brandName
                  ? `Create your first product for ${brandName} brand`
                  : "Create your first product to get started"}
              </p>
              <button
                onClick={handleCreate}
                className="bg-gradient-to-r from-[#B8460E] to-[#8B3A0E] hover:from-[#8B3A0E] hover:to-[#B8460E] text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <Plus className="w-5 h-5 inline mr-2" />
                Create Product
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      <CreateProductModal
        isOpen={showCreateModal || showEditModal}
        onClose={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
          setSelectedProduct(null);
        }}
        onSuccess={handleModalSuccess}
        editProduct={selectedProduct}
        brandId={brandId}
        brandName={brandName}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Product"
        message={`Are you sure you want to delete "${productToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete Product"
        cancelText="Cancel"
        isLoading={isDeleting}
        variant="danger"
      />
    </div>
  );
};

export default ProductManagement;
